import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/PublicNav";
import { SEO } from "@/components/SEO";
import {
  QrCode,
  Smartphone,
  ShoppingCart,
  BarChart3,
  Star,
  Languages,
  ArrowRight,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="zq-card p-5">
    <div className="w-10 h-10 rounded-md flex items-center justify-center bg-zq-orange-light text-zq-orange mb-3">
      <Icon size={20} />
    </div>
    <h3 className="font-semibold text-base">{title}</h3>
    <p className="text-sm text-zq-muted mt-1.5">{desc}</p>
  </div>
);

export const Home = () => {
  return (
    <PublicShell>
      <SEO
        title="ZaykaQR — Digital Menu System for Indian Restaurants"
        description="QR-based digital menus, WhatsApp ordering with zero commission, and built-in loyalty for Indian restaurants. From ₹1,299/month."
        url="https://zaykaqr.space/"
        image="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "ZaykaQR",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "1299", "priceCurrency": "INR" },
          "description": "Digital menu, QR codes, WhatsApp ordering and loyalty for Indian restaurants.",
        }}
      />
      {/* HERO */}
      <section className="zq-hero-bg" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="zq-fade-up">
            <span className="zq-pill zq-pill-orange mb-5">Made for Indian restaurants</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-zq-charcoal leading-[1.05]">
              Desi flavour.<br />
              <span className="text-zq-orange">Digital future.</span>
            </h1>
            <p className="mt-5 text-zq-muted text-base max-w-xl">
              QR-based digital menus, WhatsApp ordering with zero commission, and a built-in loyalty system —
              everything your restaurant needs to delight customers and grow repeat orders.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/pricing" className="zq-btn zq-btn-primary" data-testid="hero-cta-primary">
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/features" className="zq-btn zq-btn-outline" data-testid="hero-cta-secondary">
                See features
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-zq-muted">
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zq-orange" /> No app install</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zq-orange" /> Zero commission</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-zq-orange" /> 3 languages</span>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative mx-auto">
            <div className="w-[300px] md:w-[340px] rounded-[36px] bg-[#1c1c1c] p-3 shadow-2xl">
              <div className="rounded-[28px] bg-zq-cream p-4 h-[600px] overflow-hidden">
                <div className="flex items-center gap-3 pb-3 border-b border-zq-border">
                  <div className="w-10 h-10 rounded-full bg-zq-orange flex items-center justify-center text-white font-bold">M</div>
                  <div>
                    <div className="font-semibold text-sm">Mehta's Kitchen</div>
                    <div className="text-[10px] text-zq-muted">DIGITAL MENU</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2 text-xs overflow-hidden">
                  <span className="zq-pill zq-pill-orange">Starters</span>
                  <span className="zq-pill zq-pill-gray">Mains</span>
                  <span className="zq-pill zq-pill-gray">Desserts</span>
                </div>
                <div className="mt-3 space-y-3">
                  {[
                    { n: "Paneer Tikka", p: 280, img: "https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?w=300" },
                    { n: "Butter Chicken", p: 360, img: "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=300" },
                    { n: "Masala Dosa", p: 180, img: "https://images.unsplash.com/photo-1668236543090-82eba5ee5976?w=300" },
                  ].map((it, i) => (
                    <div key={i} className="flex gap-3 zq-card p-2">
                      <img src={it.img} alt={it.n} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <div className="font-semibold text-sm">{it.n}</div>
                          <div className="text-[11px] text-zq-muted">Chef's special</div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm">₹{it.p}</span>
                          <span className="zq-pill zq-pill-orange text-[10px]">+ Add</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="zq-cart-bar !static !mx-0 !mt-4 !w-full !max-w-none">
                  <div className="flex items-center gap-2"><ShoppingCart size={16}/> 3 items</div>
                  <div className="font-semibold">₹820 →</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="zq-section" data-testid="home-features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="zq-pill zq-pill-orange">All-in-one</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4">Everything to go digital — minus the headache</h2>
            <p className="text-zq-muted mt-3 text-base">From QR generation to loyalty rewards, we've built every tool a modern Indian restaurant needs.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            <FeatureCard icon={QrCode} title="QR Code Generation" desc="Unique QR per restaurant — print, share, stick on tables." />
            <FeatureCard icon={Smartphone} title="Mobile-first menu" desc="Beautiful, fast, mobile-optimised menu page for customers." />
            <FeatureCard icon={ShoppingCart} title="Cart + WhatsApp orders" desc="Multi-item cart that sends ready-to-confirm WhatsApp orders." />
            <FeatureCard icon={Languages} title="Multilanguage" desc="English, हिंदी, ગુજરાતી — switch on the fly." />
            <FeatureCard icon={Star} title="Loyalty badges" desc="Reward repeat customers automatically by phone number." />
            <FeatureCard icon={BarChart3} title="Analytics" desc="QR scans, popular items, customer frequency at a glance." />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-zq-border" data-testid="home-how">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center">Live in 3 simple steps</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { n: "01", t: "Sign up", d: "Get your account created with a unique QR menu URL." },
              { n: "02", t: "Add your menu", d: "Upload items in 3 languages, set prices in ₹, mark availability." },
              { n: "03", t: "Print your QR", d: "Place on tables. Customers scan, order, and earn loyalty badges." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="text-6xl font-bold text-zq-orange/20">{s.n}</div>
                <h3 className="text-xl font-semibold mt-2">{s.t}</h3>
                <p className="text-zq-muted mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="zq-section" data-testid="home-testimonials">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center">Loved by restaurants across India</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { n: "Rohit Mehta", r: "Mehta's Kitchen, Ahmedabad", t: "Switched from Zomato-only to direct WhatsApp orders. Saved ₹40,000 in commissions in the first month itself." },
              { n: "Priya Sharma", r: "Spice Bowl, Mumbai", t: "Customers love scanning the QR. Multilanguage menus brought in older Gujarati customers we never reached online." },
              { n: "Arjun Iyer", r: "Madras Café, Bengaluru", t: "Loyalty badges made repeat visits a game. Our regulars now bring friends to earn faster!" },
            ].map((t, i) => (
              <div key={i} className="zq-card p-6">
                <div className="flex gap-1 text-zq-orange mb-3">
                  {Array.from({ length: 5 }).map((_, k) => (<Star key={k} size={16} fill="#F97316" />))}
                </div>
                <p className="text-sm text-zq-charcoal">"{t.t}"</p>
                <div className="mt-4 text-sm font-semibold">{t.n}</div>
                <div className="text-xs text-zq-muted">{t.r}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zq-charcoal text-white" data-testid="home-cta">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Ready to go digital?</h2>
          <p className="text-gray-300 mt-3 max-w-2xl mx-auto">Join hundreds of Indian restaurants delighting customers with QR menus & WhatsApp ordering.</p>
          <div className="flex justify-center gap-3 mt-7">
            <Link to="/pricing" className="zq-btn zq-btn-primary" data-testid="cta-pricing"><MessageCircle size={18}/> Start now</Link>
            <Link to="/contact" className="zq-btn zq-btn-outline" data-testid="cta-contact">Talk to us</Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
};
